import { watcherLocation } from "./watcherBootstrap";
import {initKernel} from './Kernel/Main';
import {getApiUrl} from "./Kernel/Aspect/util";
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_MULTI_WATCHER_REQUIREMENT,
  MultiWatcherRequirement,
  parseWatcherPayoutInfo,
  selectWatcherBootstrap,
  SingleWatcherLink,
  WatcherPayoutInfo,
  WatcherSession,
} from "./watcherLinks";

type InitSessionArgs = {
  authResponse: any | null;
  brokerAuthResponse: any | null;
  watcherAuthResponse: WatcherSession | null;
  watcherAuthResponses: WatcherSession[];
  multiWatcherRequirement: MultiWatcherRequirement;
  watcherPayoutInfo: WatcherPayoutInfo | null;
  userId: string;
};

const WATCHER_SESSION_KEY = "watcher_session";
const MULTI_WATCHER_SESSION_KEY = "multiwatcher_sessions";
const MULTI_WATCHER_REQUIREMENT_KEY = "multiwatcher_requirement";

// Get user_id from URL or sessionStorage (tab-specific)
function getUserId(): string {
  const urlParams = new URLSearchParams(window.location.search);
  const subaccountId = urlParams.get("subaccount");

  if (subaccountId) {
    sessionStorage.setItem("user_id", subaccountId);
    return subaccountId;
  }

  return sessionStorage.getItem("user_id") || "";
}

function getWatcherSession(): SingleWatcherLink | null {
  const watcherSession = sessionStorage.getItem(WATCHER_SESSION_KEY);
  if (!watcherSession) {
    return null;
  }

  try {
    const parsed = JSON.parse(watcherSession);
    if (
      parsed &&
      typeof parsed.userId === "string" &&
      typeof parsed.token === "string"
    ) {
      const userId = parsed.userId.trim();
      const token = parsed.token.trim();
      if (userId && token) {
        return {
          session: { userId, token },
          payoutInfo: parseWatcherPayoutInfo(parsed.payoutInfo),
        };
      }
    }
  } catch (error) {
    console.error("Failed to parse watcher session:", error);
  }

  sessionStorage.removeItem(WATCHER_SESSION_KEY);
  return null;
}

function isWatcherSessionPayload(value: unknown): value is WatcherSession {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as WatcherSession).userId === "string" &&
    typeof (value as WatcherSession).token === "string" &&
    (value as WatcherSession).userId.trim() !== "" &&
    (value as WatcherSession).token.trim() !== ""
  );
}

function getMultiWatcherSessionsFromStorage(): WatcherSession[] {
  const watcherSessions = sessionStorage.getItem(MULTI_WATCHER_SESSION_KEY);
  if (!watcherSessions) {
    return [];
  }

  try {
    const parsed = JSON.parse(watcherSessions);
    if (!Array.isArray(parsed)) {
      return [];
    }

    const sessions: WatcherSession[] = [];
    for (const session of parsed) {
      if (!isWatcherSessionPayload(session)) {
        return [];
      }
      sessions.push({
        userId: session.userId.trim(),
        token: session.token.trim(),
      });
    }
    return sessions;
  } catch (error) {
    console.error("Failed to parse multiwatcher sessions:", error);
    sessionStorage.removeItem(MULTI_WATCHER_SESSION_KEY);
    return [];
  }
}

function getMultiWatcherRequirementFromStorage(): MultiWatcherRequirement {
  const requirement = sessionStorage.getItem(MULTI_WATCHER_REQUIREMENT_KEY);
  if (
    requirement === "hashrate" ||
    requirement === "generated_btc" ||
    requirement === "generated_btc_hashrate"
  ) {
    return requirement;
  }

  return DEFAULT_MULTI_WATCHER_REQUIREMENT;
}

function storeMultiWatcherSessions(sessions: WatcherSession[]): void {
  if (sessions.length === 0) {
    sessionStorage.removeItem(MULTI_WATCHER_SESSION_KEY);
    return;
  }

  try {
    sessionStorage.setItem(MULTI_WATCHER_SESSION_KEY, JSON.stringify(sessions));
  } catch (error) {
    console.error("Failed to persist multiwatcher sessions:", error);
  }
}

function storeMultiWatcherRequirement(requirement: MultiWatcherRequirement): void {
  sessionStorage.setItem(MULTI_WATCHER_REQUIREMENT_KEY, requirement);
}

function storeWatcherSession(
  watcherSession: WatcherSession,
  payoutInfo: WatcherPayoutInfo | null
): void {
  sessionStorage.setItem(
    WATCHER_SESSION_KEY,
    JSON.stringify({ ...watcherSession, payoutInfo })
  );
  sessionStorage.removeItem("user_id");
  sessionStorage.removeItem("broker_id");
}

function initApp({
  authResponse,
  brokerAuthResponse,
  watcherAuthResponse,
  watcherAuthResponses,
  multiWatcherRequirement,
  watcherPayoutInfo,
  userId,
}: InitSessionArgs): void {
  initKernel({
    elSelector: "app-placeholder",
    initArgs: {
      configArgs: (process as any).env.config,
      authResponse,
      brokerAuthResponse,
      watcherAuthResponse,
      watcherAuthResponses,
      multiWatcherRequirement,
      watcherPayoutInfo,
      userId,
      uuids: Array.from({ length: 300000 }, () => uuidv4()),
      correlationIds: Array.from({ length: 100 }, () => uuidv4()),
      uuid: uuidv4(),
      correlationId: uuidv4(),
    },
    portHandlers: [],
    portHandlersOnReady: [],
  });
}

function initWithUserAuth(): void {
  const userId = getUserId();

  testAuth(userId)
    .then((authResponse: any) => {
      // Store the user_id in sessionStorage after successful authentication
      if (
        authResponse &&
        authResponse.id &&
        !sessionStorage.getItem("user_id")
      ) {
        sessionStorage.setItem("user_id", authResponse.id.toString());
      }
      const currentUserId = sessionStorage.getItem("user_id") || userId;

      initApp({
        authResponse: authResponse,
        brokerAuthResponse: null,
        watcherAuthResponse: null,
        watcherAuthResponses: [],
        multiWatcherRequirement: DEFAULT_MULTI_WATCHER_REQUIREMENT,
        watcherPayoutInfo: null,
        userId: currentUserId,
      });
    })
    .catch((error) => {
      console.error("Failed to initialize kernel:", error);
    });
}

const { multiWatcherLink, singleWatcherLink: singleWatcherLinkFromPath, startsWithoutStoredWatcherState } = watcherLocation;
const multiWatcherSessions = multiWatcherLink?.sessions ?? null;
const savedMultiWatcherSessions = startsWithoutStoredWatcherState
  ? []
  : getMultiWatcherSessionsFromStorage();
const watcherSessionFromPath = singleWatcherLinkFromPath?.session ?? null;
const watcherLinkFromStorage = startsWithoutStoredWatcherState
  ? null
  : getWatcherSession();
const {
  watcherSession,
  watcherAuthResponses,
  multiWatcherRequirement,
  watcherPayoutInfo,
} = selectWatcherBootstrap({
  multiWatcherLink,
  singleWatcherLink: singleWatcherLinkFromPath,
  savedMultiWatcherSessions,
  savedSingleWatcherLink: watcherLinkFromStorage,
  savedMultiWatcherRequirement: getMultiWatcherRequirementFromStorage(),
});

if (multiWatcherSessions) {
  storeMultiWatcherSessions(multiWatcherSessions);
  storeMultiWatcherRequirement(multiWatcherRequirement);
} else if (startsWithoutStoredWatcherState) {
  sessionStorage.removeItem(WATCHER_SESSION_KEY);
  sessionStorage.removeItem(MULTI_WATCHER_SESSION_KEY);
  sessionStorage.removeItem(MULTI_WATCHER_REQUIREMENT_KEY);
} else if (watcherSessionFromPath) {
  sessionStorage.removeItem(MULTI_WATCHER_SESSION_KEY);
  sessionStorage.removeItem(MULTI_WATCHER_REQUIREMENT_KEY);
}

if (watcherSession) {
  storeWatcherSession(watcherSession, watcherPayoutInfo);
  initApp({
    authResponse: null,
    brokerAuthResponse: null,
    watcherAuthResponse: watcherSession,
    watcherAuthResponses,
    multiWatcherRequirement,
    watcherPayoutInfo,
    userId: watcherSession.userId,
  });
} else if (window.self !== window.top) {
  // Only scoped watcher sessions may run in an embedded document. Never fall
  // back to operator/user/broker cookies through the frameable watcher route.
  document.body.textContent = "Open a valid watcher link to view monitoring in this frame.";
} else {
  const brokerSession = sessionStorage.getItem("broker_id");
  if (brokerSession) {
    try {
      const brokerAuthResponse = JSON.parse(brokerSession);
      initApp({
        authResponse: null,
        brokerAuthResponse: brokerAuthResponse,
        watcherAuthResponse: null,
        watcherAuthResponses: [],
        multiWatcherRequirement: DEFAULT_MULTI_WATCHER_REQUIREMENT,
        watcherPayoutInfo: null,
        userId: "",
      });
    } catch (error) {
      console.error("Failed to parse broker session:", error);
      sessionStorage.removeItem("broker_id");
      initWithUserAuth();
    }
  } else {
    initWithUserAuth();
  }
}

function testAuth(userId: string): Promise<any | null> {
  const headers: HeadersInit = {};
  if (userId) {
    headers["X-Account-ID"] = userId;
  }

  return fetch(getApiUrl() + '/api/check_auth', {
    method: 'GET',
    credentials: 'include',
    headers: headers,
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      return null;
    })
    .then((data) => {
      if (data) {
        return {
          email: data.email,
          language: data.language,
          active: data.active,
          kycStatus: "unverified",
          sellingHashRate: data.selling_hash_rate,
          twoFactorSecret: data.two_factor_secret,
          id: data.id,
          token: data.token,
          fpps_token: data.fpps_token ?? "",
          bitcoinAddresses: data.bitcoin_addresses,
        };
      }
      return null;
    })
    .catch(() => null);
}
